import type { Meta } from '@/data/types';
import type { Filters, Preset, PresetId } from './types';

/**
 * Встроенный список пресетов. Он ИТАЛЬЯНСКИЙ по смыслу: «Доломиты» — горы в
 * Италии, «Субальпийский» — её климатический пояс. Другая страна объявляет свои
 * пресеты в meta.filter_presets; список ниже остаётся фолбэком для страны,
 * которая этого ещё не сделала (сегодня — только Италия).
 */
export const PRESETS: Preset[] = [
  {
    id: 'my_profile',
    label: 'Sample profile (demo)',
    emoji: '🎯',
    filters: {
      alt_min: 800,
      alt_max: 1500,
      t_jul_max: 24,
      t_jan_min: -10,
      pop_max: 80000,
      hosp_max: 30,
    },
  },
  {
    id: 'alpine',
    label: 'Высокогорье',
    emoji: '🏔',
    filters: {
      alt_min: 800,
      alt_max: 3500,
      t_jul_max: 22,
      snow_min: 20,
    },
  },
  {
    id: 'subalpine',
    label: 'Субальпийский',
    emoji: '🌿',
    filters: {
      alt_min: 400,
      alt_max: 1500,
      t_jul_max: 26,
      t_jan_min: -5,
    },
  },
  {
    id: 'lakeside',
    label: 'Озёрный',
    emoji: '🌊',
    filters: {
      alt_min: 0,
      alt_max: 600,
      t_jul_max: 27,
      t_jan_min: -2,
      hum_max: 78,
    },
  },
  {
    id: 'dolomites',
    label: 'Доломиты',
    emoji: '⛷',
    filters: {
      alt_min: 600,
      alt_max: 3500,
      snow_min: 30,
      sunny_min: 200,
    },
  },
  {
    id: 'noheat',
    label: 'Без жары',
    emoji: '🌡',
    filters: {
      t_jul_max: 23,
      t_jan_min: -5,
    },
  },
];

export const PRESETS_BY_ID: Record<PresetId, Preset> = Object.fromEntries(
  PRESETS.map((p) => [p.id, p]),
) as Record<PresetId, Preset>;

// ─── Пресеты страны из meta.json ─────────────────────────────────────────────

/**
 * Числовые поля модели фильтров. Ключ пресета из meta, которого здесь нет,
 * игнорируется: страна не должна иметь возможность записать в стор что попало.
 */
const NUMERIC_FILTER_KEYS = [
  'alt_min',
  'alt_max',
  't_jul_max',
  't_jan_min',
  'hum_max',
  'snow_min',
  'sunny_min',
  'pop_max',
  'hosp_max',
] as const satisfies readonly (keyof Filters)[];

type NumericFilterKey = (typeof NUMERIC_FILTER_KEYS)[number];

const NUMERIC_FILTER_KEY_SET = new Set<string>(NUMERIC_FILTER_KEYS);

/**
 * Пресеты активной страны: из meta, если она их объявила, иначе — встроенные.
 * Чистая функция, поэтому компонент может звать её прямо в рендере и получать
 * реактивный результат вместе с meta.
 */
export function presetsFromMeta(meta: Meta | undefined): Preset[] {
  const declared = meta?.filter_presets;
  if (!declared || declared.length === 0) return PRESETS;
  return declared.map((p) => {
    const filters: Partial<Filters> = {};
    for (const [key, value] of Object.entries(p.filters)) {
      if (NUMERIC_FILTER_KEY_SET.has(key)) filters[key as NumericFilterKey] = value;
    }
    return { id: p.id, label: p.label_en, labelRu: p.label_ru, emoji: p.emoji, filters };
  });
}

/**
 * Реестр пресетов активной страны для НЕреактивных потребителей — стора
 * фильтров, который применяет пресет по id из URL (`#p=…`) ещё до того, как
 * панель фильтров смонтирована. Заполняется из того же presetsFromMeta().
 *
 * `null` означает «список страны ещё не известен» и НЕ подменяется встроенным
 * итальянским: иначе ссылка `/es/map#p=dolomites` применила бы к Испании
 * итальянский фильтр по снегу и обнулила выдачу. В этом состоянии применение
 * пресета откладывается до момента, когда панель получит meta страны.
 */
let countryPresets: Preset[] | null = null;

export function setCountryPresets(list: Preset[] | null): void {
  countryPresets = list;
}

export function presetById(id: PresetId): Preset | undefined {
  return countryPresets?.find((p) => p.id === id);
}
